import React, { useState, useEffect, useRef } from 'react';
import { X, Wrench, Save, Trash2, Image as ImageIcon, QrCode, Download } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import type { Tool, ToolCategory, ToolStatus } from '@shared/types/inventory';
import { v4 as uuidv4 } from 'uuid';
import { QRCodeCanvas } from 'qrcode.react';
import { ToolHistoryList } from './ToolHistoryList';

interface ToolModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (tool: Tool) => void;
    onDelete?: (toolId: string) => void;
    initialTool?: Tool | null;
    categories: ToolCategory[];
}

export const ToolModal: React.FC<ToolModalProps> = ({
    isOpen, onClose, onSave, onDelete, initialTool, categories
}) => {
    const [activeTab, setActiveTab] = useState<'details' | 'qr' | 'history'>('details');
    const [formData, setFormData] = useState<Partial<Tool>>({
        name: '',
        category: 'Electroportatif',
        status: 'available',
        serialNumber: '',
        purchaseDate: '',
        image: '',
        description: '',
        qrIdentifier: '',
        inventoryNumber: ''
    });

    const qrRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        if (initialTool) {
            setFormData(initialTool);
        } else {
            setFormData({
                id: uuidv4(), // Pre-generate ID for new tools so QR works immediately
                name: '',
                category: categories[0] || 'Electroportatif',
                status: 'available',
                serialNumber: '',
                purchaseDate: '',
                image: '',
                description: '',
                qrIdentifier: '',
                inventoryNumber: ''
            });
        }
        setActiveTab('details');
    }, [initialTool, isOpen, categories]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const toolToSave: Tool = {
            id: initialTool?.id || formData.id || uuidv4(),
            name: formData.name || 'Nouvel Outil',
            category: formData.category as ToolCategory,
            status: formData.status as ToolStatus,
            assignedTo: formData.assignedTo,
            serialNumber: formData.serialNumber,
            purchaseDate: formData.purchaseDate,
            image: formData.image,
            description: formData.description,
            qrIdentifier: formData.qrIdentifier,
            inventoryNumber: formData.inventoryNumber
        };
        onSave(toolToSave);
        onClose();
    };

    const downloadQRCode = () => {
        if (!qrRef.current) return;
        const canvas = qrRef.current;
        const url = canvas.toDataURL("image/png");
        const link = document.createElement("a");
        link.href = url;
        link.download = `qrcode-${formData.name || 'tool'}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const qrValue = formData.qrIdentifier || formData.id || 'tool-id-missing';

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                    onClick={onClose}
                />

                <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    className="bg-[#1e293b] border border-white/10 rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden relative z-10 max-h-[90vh] flex flex-col"
                >
                    {/* Header */}
                    <div className="p-4 border-b border-white/10 flex items-center justify-between flex-shrink-0">
                        <div className="flex items-center gap-4">
                            <h3 className="font-bold text-white flex items-center gap-2">
                                <Wrench size={18} className="text-primary" />
                                {initialTool ? 'Modifier Outil' : 'Nouvel Outil'}
                            </h3>

                            {/* Tabs */}
                            <div className="flex bg-black/30 p-1 rounded-lg gap-1">
                                <button
                                    onClick={() => setActiveTab('details')}
                                    className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${activeTab === 'details' ? 'bg-white/10 text-white' : 'text-gray-400 hover:text-white'}`}
                                >
                                    Détails
                                </button>
                                <button
                                    onClick={() => setActiveTab('history')}
                                    className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${activeTab === 'history' ? 'bg-white/10 text-white' : 'text-gray-400 hover:text-white'}`}
                                >
                                    Historique
                                </button>
                                <button
                                    onClick={() => setActiveTab('qr')}
                                    className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${activeTab === 'qr' ? 'bg-white/10 text-white' : 'text-gray-400 hover:text-white'}`}
                                >
                                    QR Code
                                </button>
                            </div>
                        </div>
                        <button onClick={onClose} className="p-1 hover:bg-white/10 rounded-full transition-colors">
                            <X size={20} className="text-gray-400" />
                        </button>
                    </div>

                    {/* Body */}
                    <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
                        <div className="p-6 overflow-y-auto custom-scrollbar flex-1">
                            {activeTab === 'details' ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* Name */}
                                    <div className="col-span-full">
                                        <label className="block text-xs font-medium text-gray-400 mb-1">Nom de l'outil</label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.name}
                                            onChange={e => setFormData({ ...formData, name: e.target.value })}
                                            className="w-full bg-black/20 border border-white/10 rounded-lg py-2 px-3 text-sm text-white focus:outline-none focus:border-primary/50"
                                            placeholder="Ex: Perforateur Hilti TE-30"
                                        />
                                    </div>

                                    {/* Category */}
                                    <div>
                                        <label className="block text-xs font-medium text-gray-400 mb-1">Catégorie</label>
                                        <select
                                            value={formData.category}
                                            onChange={e => setFormData({ ...formData, category: e.target.value as ToolCategory })}
                                            className="w-full bg-black/20 border border-white/10 rounded-lg py-2 px-3 text-sm text-white focus:outline-none focus:border-primary/50"
                                        >
                                            {categories.map(cat => (
                                                <option key={cat} value={cat} className="bg-slate-800">{cat}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Status */}
                                    <div>
                                        <label className="block text-xs font-medium text-gray-400 mb-1">Statut Initial</label>
                                        <select
                                            value={formData.status}
                                            onChange={e => setFormData({ ...formData, status: e.target.value as ToolStatus })}
                                            className="w-full bg-black/20 border border-white/10 rounded-lg py-2 px-3 text-sm text-white focus:outline-none focus:border-primary/50"
                                        >
                                            <option value="available" className="bg-slate-800">Disponible</option>
                                            <option value="assigned" className="bg-slate-800">Assigné</option>
                                            <option value="maintenance" className="bg-slate-800">En Maintenance</option>
                                            <option value="lost" className="bg-slate-800">Perdu / Volé</option>
                                        </select>
                                    </div>

                                    {/* Serial Number */}
                                    <div>
                                        <label className="block text-xs font-medium text-gray-400 mb-1">Numéro de Série</label>
                                        <input
                                            type="text"
                                            value={formData.serialNumber || ''}
                                            onChange={e => setFormData({ ...formData, serialNumber: e.target.value })}
                                            className="w-full bg-black/20 border border-white/10 rounded-lg py-2 px-3 text-sm text-white focus:outline-none focus:border-primary/50"
                                            placeholder="N/S..."
                                        />
                                    </div>

                                    {/* Inventory Number */}
                                    <div>
                                        <label className="block text-xs font-medium text-gray-400 mb-1">N° Inventaire</label>
                                        <input
                                            type="text"
                                            value={formData.inventoryNumber || ''}
                                            readOnly={!initialTool && !formData.inventoryNumber}
                                            onChange={e => setFormData({ ...formData, inventoryNumber: e.target.value })}
                                            className={`w-full bg-black/20 border border-white/10 rounded-lg py-2 px-3 text-sm text-white focus:outline-none focus:border-primary/50 ${!initialTool && !formData.inventoryNumber ? 'opacity-50 cursor-not-allowed italic' : ''}`}
                                            placeholder={!initialTool ? "Généré automatiquement à l'enregistrement" : "Ex: INV-2026-001"}
                                        />
                                    </div>

                                    {/* Purchase Date */}
                                    <div>
                                        <label className="block text-xs font-medium text-gray-400 mb-1">Date d'achat</label>
                                        <input
                                            type="date"
                                            value={formData.purchaseDate ? formData.purchaseDate.split('T')[0] : ''}
                                            onChange={e => setFormData({ ...formData, purchaseDate: e.target.value })}
                                            className="w-full bg-black/20 border border-white/10 rounded-lg py-2 px-3 text-sm text-white focus:outline-none focus:border-primary/50"
                                        />
                                    </div>

                                    {/* Image URL */}
                                    <div className="col-span-full">
                                        <label className="block text-xs font-medium text-gray-400 mb-1">URL Image (Optionnel)</label>
                                        <div className="flex gap-2">
                                            <input
                                                type="text"
                                                value={formData.image || ''}
                                                onChange={e => setFormData({ ...formData, image: e.target.value })}
                                                className="flex-1 bg-black/20 border border-white/10 rounded-lg py-2 px-3 text-sm text-white focus:outline-none focus:border-primary/50"
                                                placeholder="https://..."
                                            />
                                            <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
                                                {formData.image ? (
                                                    <img src={formData.image} alt="Preview" className="w-full h-full object-cover" />
                                                ) : (
                                                    <ImageIcon size={16} className="text-gray-500" />
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Description */}
                                    <div className="col-span-full">
                                        <label className="block text-xs font-medium text-gray-400 mb-1">Notes / Description</label>
                                        <textarea
                                            value={formData.description || ''}
                                            onChange={e => setFormData({ ...formData, description: e.target.value })}
                                            className="w-full bg-black/20 border border-white/10 rounded-lg py-2 px-3 text-sm text-white focus:outline-none focus:border-primary/50 min-h-[80px]"
                                            placeholder="Détails supplémentaires..."
                                        />
                                    </div>
                                </div>
                            ) : activeTab === 'history' ? (
                                <ToolHistoryList toolId={initialTool?.id} />
                            ) : (
                                <div className="flex flex-col items-center justify-center space-y-6 py-4">
                                    <div className="bg-white p-4 rounded-xl shadow-lg">
                                        <QRCodeCanvas
                                            value={qrValue}
                                            size={200}
                                            level={"H"}
                                            includeMargin={true}
                                            ref={qrRef}
                                        />
                                    </div>

                                    <div className="w-full max-w-md space-y-4">
                                        <div>
                                            <label className="block text-xs font-medium text-gray-400 mb-1">Valeur du Code QR</label>
                                            <div className="flex gap-2">
                                                <div className="relative flex-1">
                                                    <QrCode className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
                                                    <input
                                                        type="text"
                                                        value={formData.qrIdentifier || ''}
                                                        onChange={e => setFormData({ ...formData, qrIdentifier: e.target.value })}
                                                        placeholder={`Défaut: ${formData.id ? formData.id.substring(0, 8) + '...' : 'ID auto'}`}
                                                        className="w-full bg-black/20 border border-white/10 rounded-lg py-2 pl-9 pr-3 text-sm text-white focus:outline-none focus:border-primary/50 font-mono"
                                                    />
                                                </div>
                                            </div>
                                            <p className="text-[10px] text-gray-500 mt-1">
                                                Laissez vide pour utiliser l'identifiant unique du système.
                                            </p>
                                        </div>

                                        <button
                                            type="button"
                                            onClick={downloadQRCode}
                                            className="w-full py-2 bg-secondary/10 hover:bg-secondary/20 text-secondary border border-secondary/50 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                                        >
                                            <Download size={16} />
                                            Télécharger l'image PNG
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Actions */}
                        <div className="p-4 border-t border-white/10 flex items-center justify-between gap-3 flex-shrink-0 bg-[#1e293b]">
                            {initialTool && onDelete ? (
                                <button
                                    type="button"
                                    onClick={() => onDelete(initialTool.id)}
                                    className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-500 border border-red-500/50 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                                >
                                    <Trash2 size={16} />
                                    Supprimer
                                </button>
                            ) : (
                                <div></div>
                            )}

                            <div className="flex items-center gap-3">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-4 py-2 hover:bg-white/5 text-gray-400 hover:text-white border border-transparent rounded-lg text-sm font-medium transition-colors"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2 shadow-lg shadow-primary/20"
                                >
                                    <Save size={16} />
                                    Enregistrer
                                </button>
                            </div>
                        </div>
                    </form>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};
